import React, { useRef, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, TextField, Typography } from '@mui/material';
import {
  Check,
  ChevronRight,
  FileUploadOutlined,
  ManageSearch,
  PriorityHigh,
  ShieldOutlined,
} from '@mui/icons-material';
import { tokens } from '../../theme/tokens';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  BrandAuthorizationCheckResult,
  BrandAuthorizationProof,
  BrandVerificationPath,
  PROOF_ACCEPTED_TYPES,
  isValidProofLink,
  matchedStoreLabel,
  methodLabel,
  verificationSourceOf,
} from '../../utils/brandAuthorization';

/** The card's look: the four states of the handoff plus "found, Motzu confirming". */
export type VerifyCardState = 'idle' | 'checking' | 'verified' | 'failed' | 'confirming';

/**
 * What the card says, before any visuals. `result` is the latest check (from
 * this session or read off the record), `checkError` a check that could not
 * run, and `status` the record's own server status for the idle wording.
 */
export interface VerifyCardModel {
  state: VerifyCardState;
  /** Which copy the card uses inside that state */
  variant:
    | 'idle'
    | 'idle_new'
    | 'review'
    | 'expired'
    | 'checking'
    | 'verified'
    | 'not_found'
    | 'error'
    | 'confirming'
    | 'held'
    | 'unavailable'
    | 'proof_only'
    | 'no_list'
    | 'rejected';
  /** The manual path is the only one: Advanced opens and drops its "Advanced:" prefix */
  manualOnly: boolean;
  /** Show the Verify now / Re-check / Try again button */
  showButton: boolean;
}

export function verifyCardModel(args: {
  checking: boolean;
  result: BrandAuthorizationCheckResult | null;
  checkError: string | null;
  path: BrandVerificationPath | null;
  status: string | null;
  isNew: boolean;
}): VerifyCardModel {
  const { checking, result, checkError, path, status, isNew } = args;
  if (checking) return { state: 'checking', variant: 'checking', manualOnly: false, showButton: true };
  if (checkError) return { state: 'failed', variant: 'error', manualOnly: false, showButton: true };
  switch (result?.outcome) {
    case 'verified':
      return { state: 'verified', variant: 'verified', manualOnly: false, showButton: true };
    case 'confirming':
      return { state: 'confirming', variant: 'confirming', manualOnly: false, showButton: true };
    case 'held':
      return { state: 'confirming', variant: 'held', manualOnly: false, showButton: true };
    case 'not_found':
      return { state: 'failed', variant: 'not_found', manualOnly: false, showButton: true };
    case 'unavailable':
      // Nothing was checked: looks like the error state, never like a miss.
      // A brand with no automatic check offers no button that would do nothing.
      return {
        state: 'failed',
        variant: 'unavailable',
        manualOnly: result?.unavailableReason === 'not_configured',
        showButton: result?.unavailableReason !== 'not_configured',
      };
    case 'rejected':
      // A check never reopens a rejected claim, so offering one would do nothing.
      return { state: 'failed', variant: 'rejected', manualOnly: true, showButton: false };
    case 'no_list':
      return { state: 'idle', variant: 'no_list', manualOnly: true, showButton: false };
    case 'proof_only':
      return { state: 'idle', variant: 'proof_only', manualOnly: true, showButton: false };
    default:
      break;
  }
  if (path === 'letter_only') return { state: 'idle', variant: 'proof_only', manualOnly: true, showButton: false };
  if (status === 'pending') return { state: 'idle', variant: 'review', manualOnly: false, showButton: true };
  if (status === 'expired') return { state: 'idle', variant: 'expired', manualOnly: false, showButton: true };
  return { state: 'idle', variant: isNew ? 'idle_new' : 'idle', manualOnly: false, showButton: true };
}

const STATE_COLORS: Record<VerifyCardState, { bg: string; border: string; iconBg: string; iconInk: string }> = {
  idle: { bg: tokens.surface.sunken, border: tokens.line.default, iconBg: tokens.line.default, iconInk: tokens.ink.bodyAlt },
  checking: { bg: tokens.surface.sunken, border: tokens.line.default, iconBg: tokens.accent.tintStrong, iconInk: tokens.accent.main },
  verified: {
    bg: tokens.signal.positive.tint,
    border: tokens.signal.positive.border,
    iconBg: tokens.signal.positive.border,
    iconInk: tokens.signal.positive.ink,
  },
  failed: {
    bg: tokens.signal.caution.tint,
    border: tokens.signal.caution.border,
    iconBg: tokens.signal.caution.border,
    iconInk: tokens.signal.caution.ink,
  },
  confirming: {
    bg: tokens.signal.info.row,
    border: tokens.signal.info.border,
    iconBg: tokens.accent.tintStrong,
    iconInk: tokens.signal.info.ink,
  },
};

/** "just now", "3 days ago", in the app's language. */
function relativeTime(iso: string | null | undefined, language: string): string {
  const then = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(then)) return '';
  const seconds = Math.round((then - Date.now()) / 1000);
  const format = new Intl.RelativeTimeFormat(language || 'en', { numeric: 'auto' });
  const abs = Math.abs(seconds);
  if (abs < 60) return format.format(0, 'second');
  if (abs < 3600) return format.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return format.format(Math.round(seconds / 3600), 'hour');
  if (abs < 86400 * 45) return format.format(Math.round(seconds / 86400), 'day');
  if (abs < 86400 * 365) return format.format(Math.round(seconds / (86400 * 30)), 'month');
  return format.format(Math.round(seconds / (86400 * 365)), 'year');
}

const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const fieldLabelSx = { fontSize: 13, fontWeight: 600, color: tokens.ink.body } as const;
const optionalSx = { fontSize: 12, color: tokens.ink.faint } as const;
const helperSx = { fontSize: 12.5, color: tokens.ink.muted } as const;

interface BrandVerificationCardProps {
  brandName: string;
  model: VerifyCardModel;
  result: BrandAuthorizationCheckResult | null;
  checkError: string | null;
  /** The brand's verification path, when known (an existing record or a check) */
  path: BrandVerificationPath | null;
  canCheck: boolean;
  onCheck: () => void;
  advancedOpen: boolean;
  onToggleAdvanced: () => void;
  /** The failed-check save rule applies: Advanced shows "Required" */
  proofRequired: boolean;
  /** Save was refused for want of proof: the drop zone turns amber */
  highlightDropZone: boolean;
  proofFile: File | null;
  onProofFile: (file: File | null) => void;
  existingProof: BrandAuthorizationProof | null;
  proofLink: string;
  onProofLink: (value: string) => void;
  existingProofLink: string | null;
  proofNote: string;
  onProofNote: (value: string) => void;
  attachedCount: number;
  /** Upload or file-type problems, shown above the drop zone */
  fileMessage: React.ReactNode;
}

/**
 * The verification block of the brand dialog: the status row with "Verify
 * now", and the "Advanced: verify manually" section for proof (file, https
 * link, note for the reviewer). Layout and states follow the MOT-280 handoff;
 * colours come from theme tokens.
 */
const BrandVerificationCard: React.FC<BrandVerificationCardProps> = ({
  brandName,
  model,
  result,
  checkError,
  path,
  canCheck,
  onCheck,
  advancedOpen,
  onToggleAdvanced,
  proofRequired,
  highlightDropZone,
  proofFile,
  onProofFile,
  existingProof,
  proofLink,
  onProofLink,
  existingProofLink,
  proofNote,
  onProofNote,
  attachedCount,
  fileMessage,
}) => {
  const { t, i18n } = useAppTranslation();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const colors = STATE_COLORS[model.state];
  const k = 'store.brandAuthorization.modal';
  const brand = brandName || t(`${k}.thisBrand`);
  const source: 'Locator' | 'List' | 'Unknown' = path === 'locator' ? 'Locator' : path === 'dealer_list' ? 'List' : 'Unknown';

  let title = '';
  let body = '';
  switch (model.variant) {
    case 'idle':
      title = t(`${k}.card.idle.title`);
      body = t(`${k}.card.idle.body${source}`, { brand });
      break;
    case 'idle_new':
      title = t(`${k}.card.idle.title`);
      body = t(`${k}.card.idle.bodyNew`, { brand });
      break;
    case 'review':
      title = t(`${k}.card.review.title`);
      body = t(`${k}.card.review.body`);
      break;
    case 'expired':
      title = t(`${k}.card.expired.title`);
      body = t(`${k}.card.expired.body`, { brand });
      break;
    case 'checking':
      title = t(`${k}.card.checking.title${source}`, { brand });
      body = t(`${k}.card.checking.body`);
      break;
    case 'verified': {
      const from = verificationSourceOf(result?.method);
      title = t(`${k}.card.verified.title${from === 'locator' ? 'Locator' : from === 'list' ? 'List' : 'Motzu'}`, { brand });
      const when = relativeTime(result?.verifiedAt || result?.checkedAt, i18n.language);
      const store = matchedStoreLabel(result?.match);
      body = store
        ? t(`${k}.card.verified.matched`, { store, when })
        : t(`${k}.card.verified.method`, { method: methodLabel(result?.method), when });
      break;
    }
    case 'not_found':
      title = t(`${k}.card.failed.title`);
      body =
        result?.path === 'locator'
          ? t(`${k}.card.failed.bodyLocator`, { brand })
          : result?.listDate
            ? t(`${k}.card.failed.bodyListDated`, { brand, date: result.listDate })
            : t(`${k}.card.failed.bodyList`, { brand });
      break;
    case 'unavailable':
      if (result?.unavailableReason === 'no_address') {
        title = t(`${k}.card.unavailable.noAddressTitle`);
        body = t(`${k}.card.unavailable.noAddressBody`, { brand });
      } else if (result?.unavailableReason === 'not_configured') {
        title = t(`${k}.card.unavailable.notConfiguredTitle`, { brand });
        body = t(`${k}.card.unavailable.notConfiguredBody`);
      } else {
        title = t(`${k}.card.unavailable.title`);
        body = t(`${k}.card.unavailable.body${source}`, { brand });
      }
      break;
    case 'error':
      title = t(`${k}.card.error.title`);
      body = t(`${k}.card.error.body`, { error: checkError || '' });
      break;
    case 'confirming': {
      const listBrand = result?.listBrandName || brand;
      const store = matchedStoreLabel(result?.match) || t(`${k}.card.confirming.yourStore`);
      title = t(`${k}.card.confirming.title`, { brand: listBrand });
      body =
        result?.reason === 'partial_name'
          ? t(`${k}.card.confirming.bodyPartial`, { brand: listBrand, store })
          : result?.reason === 'stale_list'
            ? t(`${k}.card.confirming.bodyStale`, { brand: listBrand, store })
            : t(`${k}.card.confirming.bodyAwaiting`, { brand: listBrand, store });
      break;
    }
    case 'held':
      title = t(`${k}.card.held.title`);
      body = t(`${k}.card.held.body`);
      break;
    case 'proof_only':
      title = t(`${k}.card.proofOnly.title`, { brand });
      body = t(`${k}.card.proofOnly.body`, { brand });
      break;
    case 'no_list':
      title = t(`${k}.card.noList.title`, { brand });
      body = t(`${k}.card.noList.body`, { brand });
      break;
    case 'rejected':
      title = t(`${k}.card.rejected.title`);
      body = t(`${k}.card.rejected.body`);
      break;
  }

  const buttonLabel =
    model.state === 'checking'
      ? t(`${k}.buttons.checking`)
      : model.state === 'verified' || model.state === 'confirming'
        ? t(`${k}.buttons.recheck`)
        : model.state === 'failed'
          ? t(`${k}.buttons.tryAgain`)
          : t(`${k}.buttons.verifyNow`);
  const primaryButton = model.state === 'idle';

  const icon =
    model.state === 'checking' ? (
      <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} aria-hidden />
    ) : model.state === 'verified' ? (
      <Check sx={{ fontSize: 18 }} />
    ) : model.state === 'failed' ? (
      <PriorityHigh sx={{ fontSize: 18 }} />
    ) : model.state === 'confirming' ? (
      <ManageSearch sx={{ fontSize: 18 }} />
    ) : (
      <ShieldOutlined sx={{ fontSize: 18 }} />
    );

  const linkValid = isValidProofLink(proofLink);
  const pickFile = (file: File | null | undefined) => {
    if (file) onProofFile(file);
  };

  return (
    <Box
      data-testid="verification-card"
      data-state={model.state}
      sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: colors.border, borderRadius: '12px' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.75,
          bgcolor: colors.bg,
          borderRadius: '11px 11px 0 0',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box
          sx={{
            flex: '0 0 auto',
            display: 'grid',
            placeItems: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: colors.iconBg,
            color: colors.iconInk,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }} role="status" aria-live="polite">
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink.primary }}>{title}</Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, textWrap: 'pretty' }}>{body}</Typography>
        </Box>
        {model.showButton && (
          <Button
            onClick={onCheck}
            disabled={model.state === 'checking' || !canCheck}
            variant={primaryButton ? 'contained' : 'outlined'}
            disableElevation
            sx={{
              flex: '0 0 auto',
              height: 36,
              px: 1.75,
              borderRadius: '9px',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              textTransform: 'none',
              ...(primaryButton
                ? {}
                : { bgcolor: tokens.surface.card, color: tokens.ink.primary, borderColor: tokens.line.default }),
              '&.Mui-disabled': model.state === 'checking' ? { opacity: 0.6 } : {},
            }}
          >
            {buttonLabel}
          </Button>
        )}
      </Box>

      <ButtonBase
        onClick={onToggleAdvanced}
        aria-expanded={advancedOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          px: 2,
          py: 1.375,
          borderTop: '1px solid',
          borderColor: colors.border,
          borderRadius: advancedOpen ? 0 : '0 0 11px 11px',
          bgcolor: tokens.surface.card,
          textAlign: 'left',
          '&:hover': { bgcolor: tokens.surface.sunken },
        }}
      >
        <ChevronRight
          sx={{
            fontSize: 16,
            color: tokens.ink.muted,
            transform: advancedOpen ? 'rotate(90deg)' : 'none',
            transition: 'transform .15s',
          }}
        />
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.primary }}>
          {model.state === 'failed' || model.manualOnly ? t(`${k}.advanced.titleManual`) : t(`${k}.advanced.title`)}
        </Typography>
        {proofRequired && (
          <Box
            component="span"
            sx={{
              px: 0.875,
              py: '1px',
              borderRadius: '5px',
              bgcolor: tokens.signal.caution.tint,
              color: tokens.signal.caution.ink,
              border: '1px solid',
              borderColor: tokens.signal.caution.border,
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            {t(`${k}.advanced.required`)}
          </Box>
        )}
        <Typography component="span" sx={{ ml: 'auto', fontSize: 12, color: tokens.ink.muted }}>
          {attachedCount
            ? t(`${k}.advanced.attached`, { count: attachedCount })
            : model.state === 'verified'
              ? ''
              : t(`${k}.advanced.meta`)}
        </Typography>
      </ButtonBase>

      {advancedOpen && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: 0.5,
            pr: 2,
            pb: 2,
            pl: { xs: 2, sm: 4.75 },
            bgcolor: tokens.surface.card,
            borderRadius: '0 0 11px 11px',
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.bodyAlt, lineHeight: 1.5, textWrap: 'pretty' }}>
            {t(`${k}.advanced.intro`, { brand })}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography sx={fieldLabelSx}>{t(`${k}.upload.label`)}</Typography>
            {fileMessage}
            <input
              ref={fileInput}
              hidden
              type="file"
              accept={PROOF_ACCEPTED_TYPES}
              data-testid="proof-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Allow choosing the same file again after a rejected selection.
                e.target.value = '';
                pickFile(file);
              }}
            />
            {proofFile ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  minHeight: 48,
                  px: 1.5,
                  border: '1px solid',
                  borderColor: tokens.line.default,
                  borderRadius: '10px',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '6px',
                    bgcolor: tokens.accent.tint,
                    color: tokens.accent.main,
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {proofFile.type === 'application/pdf' ? 'PDF' : 'IMG'}
                </Box>
                <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>{proofFile.name}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted }}>{formatBytes(proofFile.size)}</Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => onProofFile(null)}
                  sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink.muted, textTransform: 'none', '&:hover': { color: tokens.signal.problem.ink } }}
                >
                  {t(`${k}.upload.remove`)}
                </Button>
              </Box>
            ) : (
              <>
                {existingProof && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      minHeight: 48,
                      px: 1.5,
                      border: '1px solid',
                      borderColor: tokens.line.default,
                      borderRadius: '10px',
                    }}
                  >
                    <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                        {t(`${k}.upload.onFile`, { file: existingProof.fileName })}
                      </Typography>
                    </Box>
                    {existingProof.url && (
                      <Button size="small" href={existingProof.url} target="_blank" rel="noreferrer" sx={{ textTransform: 'none', fontWeight: 600 }}>
                        {t(`${k}.upload.open`)}
                      </Button>
                    )}
                  </Box>
                )}
                <ButtonBase
                  onClick={() => fileInput.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    pickFile(e.dataTransfer.files?.[0]);
                  }}
                  data-testid="proof-drop-zone"
                  data-highlight={highlightDropZone ? 'true' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    minHeight: 64,
                    px: 2,
                    border: '1.5px dashed',
                    borderColor: dragging
                      ? tokens.accent.main
                      : highlightDropZone
                        ? tokens.signal.caution.solid
                        : tokens.ink.disabled,
                    borderRadius: '10px',
                    bgcolor: dragging ? tokens.signal.info.row : tokens.surface.sunken,
                    fontSize: 13,
                    color: tokens.ink.bodyAlt,
                    '&:hover': { borderColor: tokens.accent.main, bgcolor: tokens.signal.info.row },
                  }}
                >
                  <FileUploadOutlined sx={{ fontSize: 18, color: tokens.accent.main }} />
                  <span>
                    <Box component="b" sx={{ color: tokens.accent.main, fontWeight: 600 }}>
                      {existingProof ? t(`${k}.upload.replace`) : t(`${k}.upload.choose`)}
                    </Box>{' '}
                    {t(`${k}.upload.drop`)}
                  </span>
                </ButtonBase>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography component="label" htmlFor="brand-auth-proof-link" sx={fieldLabelSx}>
              {t(`${k}.link.label`)}
            </Typography>
            <TextField
              id="brand-auth-proof-link"
              size="small"
              value={proofLink}
              onChange={(e) => onProofLink(e.target.value)}
              placeholder="https://"
              error={!linkValid}
              inputProps={{ maxLength: 500, inputMode: 'url' }}
              InputProps={{
                sx: { borderRadius: '9px', fontSize: 14, height: 40 },
                endAdornment:
                  existingProofLink && existingProofLink === proofLink.trim() ? (
                    <Button size="small" href={existingProofLink} target="_blank" rel="noreferrer" sx={{ textTransform: 'none', fontWeight: 600 }}>
                      {t(`${k}.upload.open`)}
                    </Button>
                  ) : undefined,
              }}
              fullWidth
            />
            <Typography sx={{ fontSize: 12, color: linkValid ? tokens.ink.muted : tokens.signal.problem.ink }}>
              {linkValid ? t(`${k}.link.help`) : t(`${k}.link.invalid`)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography component="label" htmlFor="brand-auth-proof-note" sx={fieldLabelSx}>
                {t(`${k}.note.label`)}
              </Typography>
              <Typography component="span" sx={optionalSx}>{t(`${k}.optional`)}</Typography>
            </Box>
            <TextField
              id="brand-auth-proof-note"
              value={proofNote}
              onChange={(e) => onProofNote(e.target.value)}
              placeholder={t(`${k}.note.placeholder`)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 1000, style: { resize: 'vertical' } }}
              InputProps={{ sx: { borderRadius: '9px', fontSize: 14 } }}
              fullWidth
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const verificationFieldStyles = { fieldLabelSx, optionalSx, helperSx };

export default BrandVerificationCard;
